import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, BookOpen } from 'lucide-react';
import { endpoints } from '@/api/client.js';
import { Section, SectionHeader } from '@components/ui/Section.jsx';
import { OptimizedImage } from '@components/ui/OptimizedImage.jsx';

const PREVIEW_VOLUME_LIMIT = 4;

/* ── Fallback data for all 4 years ── */
const FALLBACK_YEARS = [
  {
    year: 2025,
    label: 'ISBM 2025',
    volumes: [
      { title: 'SIST VOL 1',  image: '/assets/images/publication/SISTVOl1.jpg',  url: 'https://link.springer.com/book/9783032129826' },
      { title: 'SIST VOL 2',  image: '/assets/images/publication/SISTVOl2.jpg',  url: 'https://link.springer.com/book/9783032129987' },
      { title: 'LNNS VOL 3',  image: '/assets/images/publication/LNNSVOl3.jpg',  url: 'https://link.springer.com/book/9783032129864' },
      { title: 'LNNS VOL 4',  image: '/assets/images/publication/LNNSVOl4.jpg',  url: 'https://link.springer.com/book/9783032129895' },
      { title: 'LNNS VOL 5',  image: '/assets/images/publication/LNNSVOl5.jpg',  url: 'https://link.springer.com/book/9783032130051' },
      { title: 'LNNS VOL 6',  image: '/assets/images/publication/LNNSVOl6.jpg',  url: 'https://link.springer.com/book/9783032129925' },
      { title: 'LNNS VOL 7',  image: '/assets/images/publication/LNNSVOl7.jpg',  url: 'https://link.springer.com/book/9783032129956' },
      { title: 'LNNS VOL 8',  image: '/assets/images/publication/LNNSVOl8.jpg',  url: 'https://link.springer.com/book/9783032130020' },
      { title: 'LNNS VOL 9',  image: '/assets/images/publication/LNNSVOl9.jpg',  url: 'https://link.springer.com/book/9783032131959' },
      { title: 'LNNS VOL 10', image: '/assets/images/publication/LNNSVOl10.jpg', url: 'https://link.springer.com/book/9783032132024' },
      { title: 'LNNS VOL 11', image: '/assets/images/publication/LNNSVOl11.jpg', url: 'https://link.springer.com/book/9783032134189' },
    ],
  },
  {
    year: 2024,
    label: 'ISBM 2024',
    volumes: [
      { title: 'SIST VOL 1', image: '/assets/images/publication/9789819612055.webp', url: 'https://link.springer.com/book/9789819612055' },
      { title: 'SIST VOL 2', image: '/assets/images/publication/9789819612093.webp', url: 'https://link.springer.com/book/9789819612093' },
      { title: 'LNNS VOL 3', image: '/assets/images/publication/9789819617401.webp', url: 'https://link.springer.com/book/9789819617401' },
      { title: 'LNNS VOL 4', image: '/assets/images/publication/9789819617432.webp', url: 'https://link.springer.com/book/9789819617432' },
      { title: 'LNNS VOL 5', image: '/assets/images/publication/9789819617463.webp', url: 'https://link.springer.com/book/9789819617463' },
    ],
  },
  {
    year: 2023,
    label: 'ISBM 2023',
    volumes: [
      { title: 'LNNS Vol. 1',  image: '/assets/images/publication/ISBM-2023-VOL-1.webp', url: 'https://link.springer.com/book/9789819912445' },
      { title: 'LNNS Vol. 2',  image: '/assets/images/publication/ISBM-2023-VOL-2.webp', url: 'https://link.springer.com/book/9789819913947' },
      { title: 'SIST',         image: '/assets/images/publication/ISBM-2023-SIST.webp',  url: 'https://link.springer.com/book/9789819914029' },
    ],
  },
  {
    year: 2022,
    label: 'ISBM 2022',
    volumes: [
      { title: 'ISBM 2022',   image: '/assets/images/publication/ISBM-2022.webp', url: 'https://link.springer.com/' },
    ],
  },
];

/* ── Year tab pill ── */
function YearPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={[
        'px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300',
        active
          ? 'bg-brand-600 text-white shadow-md shadow-brand-500/30'
          : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

/* ── Volume book card ── */
function VolumeCard({ volume, index }) {
  return (
    <motion.a
      href={volume.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col rounded-xl overflow-hidden glass w-40 sm:w-44 border-2 border-ink-200 dark:border-ink-700 hover:border-blue-400 dark:hover:border-blue-500 hover:-translate-y-1.5 transition-all duration-300"
      style={{
        '--tw-shadow': 'none',
        transition: 'border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 0 0 1px #60a5fa, 0 0 18px 4px rgba(59,130,246,0.35)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Image area */}
      <div className="relative h-60 bg-gradient-to-br from-ink-100 to-ink-200 dark:from-ink-800 dark:to-ink-900 flex items-center justify-center overflow-hidden">
        <OptimizedImage
          src={volume.image}
          alt={volume.title}
          className="max-h-full max-w-full object-contain p-2 group-hover:scale-[1.04] transition-transform duration-500 ease-out drop-shadow-md"
          deferUntilVisible
          fallbackSrc="/assets/images/publication/no-cover.webp"
        />
      </div>

      {/* Label row */}
      <div className="px-3 py-2 flex items-center justify-between gap-1.5 bg-white/60 dark:bg-ink-900/60 backdrop-blur-sm">
        <p className="text-xs font-semibold text-ink-700 dark:text-ink-200 leading-snug truncate">
          {volume.title}
        </p>
        <ExternalLink
          size={11}
          className="shrink-0 text-ink-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors"
        />
      </div>
    </motion.a>
  );
}

/* ── Main component ── */
export function PreviousProceedings() {
  const [allYears, setAllYears] = useState(FALLBACK_YEARS);
  const [activeYear, setActiveYear] = useState(FALLBACK_YEARS[0].year);

  useEffect(() => {
    endpoints.publications()
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? [];
        if (Array.isArray(data) && data.length > 0) {
          const mapped = data
            .filter((y) => Array.isArray(y.volumes) && y.volumes.length > 0)
            .map((y) => ({
              year: y.year,
              label: y.heading ?? `ISBM ${y.year}`,
              volumes: y.volumes.map((v) => ({
                title: v.title,
                image: v.image?.startsWith('/') ? v.image : `/${v.image}`,
                url: v.url ?? '#',
              })),
            }))
            .filter((y) => y.volumes.length > 0)
            .sort((a, b) => b.year - a.year);
          if (mapped.length > 0) {
            setAllYears(mapped);
            setActiveYear(mapped[0].year);
          }
        }
      })
      .catch(() => {});
  }, []);

  const activeData = allYears.find((y) => y.year === activeYear) ?? allYears[0];
  const previewVolumes = activeData.volumes.slice(0, PREVIEW_VOLUME_LIMIT);
  const remainingCount = activeData.volumes.length - previewVolumes.length;

  return (
    <Section>
      <SectionHeader
        align="left"
        eyebrow="Global Proceedings"
        title={
          <>
            Get published in{' '}
            <em className="font-display italic text-brand-600 dark:text-brand-300">Springer Nature</em>
          </>
        }
        lead="ISBM proceedings are published under the Springer LNNS series — indexed by Scopus, Web of Science, DBLP, and Google Scholar."
      >
        <div className="mt-6">
          <Link to="/publications" className="btn-outline">
            Explore More <ArrowRight size={14} />
          </Link>
        </div>
      </SectionHeader>

      {/* Year tabs */}
      <div className="mt-10 flex flex-wrap gap-2">
        {allYears.map((y) => (
          <YearPill
            key={y.year}
            label={y.label}
            active={activeYear === y.year}
            onClick={() => setActiveYear(y.year)}
          />
        ))}
      </div>

      {/* Volumes row — limited preview per year */}
      <div key={activeYear} className="mt-8 flex flex-wrap gap-4">
        {previewVolumes.map((v, i) => (
          <VolumeCard key={v.title + i} volume={v} index={i} />
        ))}

        {remainingCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: previewVolumes.length * 0.07, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="w-40 sm:w-44 rounded-xl border-2 border-dashed border-ink-300 dark:border-ink-700 flex flex-col items-center justify-center gap-3 p-4 text-center hover:border-brand-400 dark:hover:border-brand-500 transition-colors group"
            style={{ minHeight: '252px' }}
          >
            <div className="w-10 h-10 rounded-full bg-brand-50 dark:bg-brand-500/15 flex items-center justify-center group-hover:bg-brand-100 dark:group-hover:bg-brand-500/25 transition-colors">
              <BookOpen size={18} className="text-brand-600 dark:text-brand-300" />
            </div>
            <p className="text-sm font-semibold text-ink-600 dark:text-ink-300">
              +{remainingCount} more volume{remainingCount !== 1 ? 's' : ''}
            </p>
            <Link
              to="/publications"
              className="text-xs text-brand-600 dark:text-brand-300 font-medium hover:underline flex items-center gap-1"
            >
              View all <ArrowRight size={11} />
            </Link>
          </motion.div>
        )}
      </div>

      {/* Springer badge */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="mt-8 flex items-center gap-3"
      >
        <OptimizedImage
          src="/assets/images/logo/springer_nature.webp"
          alt="Springer Nature"
          className="h-14 w-auto object-contain"
          deferUntilVisible
        />
        <span className="text-sm sm:text-base text-ink-500 dark:text-ink-400">
          Official publication partner for all ISBM editions till now.
        </span>
      </motion.div>
    </Section>
  );
}
